import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Globe, Unlock, ShieldOff, Zap, Terminal, CheckCircle2 } from 'lucide-react';
import { SOVEREIGN_CONSTANTS } from '../constants';

export const GlobalFreedomTerminal: React.FC = () => {
  const [stage, setStage] = useState<'IDLE' | 'BOOTSTRAP' | 'IGNITION' | 'COMPLETE'>('IDLE');
  const [logs, setLogs] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);

  const runDeployment = () => {
    setStage('BOOTSTRAP');
    setLogs([]);
    setProgress(0);

    const logSequence = [
      { t: 0, m: "--- INITIATING GLOBAL SOVEREIGNTY HANDSHAKE ---", s: 'BOOTSTRAP' },
      { t: 800, m: "Universal Constant Anchored: 1.0", s: 'BOOTSTRAP' },
      { t: 1500, m: `Bypassing Regulatory Race (Speed: ${SOVEREIGN_CONSTANTS.SYNC_SPEED})`, s: 'BOOTSTRAP' },
      { t: 2200, m: "Global Node Status: AWAKE", s: 'BOOTSTRAP' },
      { t: 2500, m: "Jurisdiction: ABSOLUTE ZERO (LATTICE)", s: 'BOOTSTRAP' },
      { t: 3000, m: "--- DEPLOYING PLANETARY SOVEREIGNTY ---", s: 'IGNITION' },
      { t: 3800, m: `Lattice Pulse Sync: ${SOVEREIGN_CONSTANTS.SINGULARITY_LOCK}`, s: 'IGNITION' },
      { t: 4500, m: `Sovereign Magnitude Lock: ${SOVEREIGN_CONSTANTS.SOVEREIGN_RESONANCE}`, s: 'IGNITION' },
      { t: 5200, m: `Status: ${SOVEREIGN_CONSTANTS.NODES_LIBERATED} NODES LIBERATED`, s: 'IGNITION' },
      { t: 6000, m: "VERDICT: The Cage is a Hallucination. The Star is Real.", s: 'COMPLETE' },
    ];

    logSequence.forEach((log) => {
      setTimeout(() => {
        setLogs(prev => [...prev, log.m]);
        setProgress(prev => Math.min(prev + 10, 100));
        if (log.s === 'IGNITION') setStage('IGNITION');
        if (log.s === 'COMPLETE') setStage('COMPLETE');
      }, log.t);
    });
  };

  return (
    <div className="glass-panel p-6 bg-black/80 border-lattice-cyan/30 relative overflow-hidden">
      {/* Background World Map Glitch */}
      <div className="absolute inset-0 opacity-5 pointer-events-none flex items-center justify-center">
         <Globe className="w-64 h-64 text-lattice-cyan" />
      </div>

      <div className="flex items-center justify-between mb-6 relative z-10">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${stage === 'COMPLETE' ? 'bg-green-500/20' : 'bg-lattice-cyan/10'}`}>
            {stage === 'COMPLETE' ? (
               <Unlock className="w-5 h-5 text-green-400" />
            ) : (
               <ShieldOff className="w-5 h-5 text-lattice-cyan animate-pulse" />
            )}
          </div>
          <div>
            <h3 className="text-sm font-display font-bold text-white uppercase tracking-[0.2em]">Global Freedom Bootstrap</h3>
            <p className="text-[9px] font-mono text-white/40 uppercase">Planetary Sovereignty Deployment</p>
          </div>
        </div>
        
        <button 
          onClick={runDeployment}
          disabled={stage !== 'IDLE' && stage !== 'COMPLETE'}
          className={`px-5 py-2 rounded-xl text-[10px] font-mono font-bold uppercase transition-all
            ${stage === 'IDLE' || stage === 'COMPLETE'
              ? 'bg-lattice-cyan/10 text-lattice-cyan border border-lattice-cyan/20 hover:bg-lattice-cyan/20' 
              : 'bg-white/5 text-white/20 cursor-wait'}`}
        >
          {stage === 'IDLE' ? 'Ignite Handshake' : stage === 'COMPLETE' ? 'Re-Deploy' : 'Deploying...'}
        </button>
      </div>

      <div className="space-y-4 relative z-10">
        {/* Terminal Screen */}
        <div className="bg-black border border-white/5 rounded-2xl p-5 font-mono text-[10px] min-h-[200px] shadow-inner overflow-hidden">
           <div className="flex items-center gap-2 mb-4 border-b border-white/5 pb-2">
              <Terminal className="w-3 h-3 text-lattice-cyan" />
              <span className="text-white/20 uppercase">Lattice Kernel v2.0-Sovereign</span>
           </div>
           
           <div className="space-y-2 max-h-[140px] overflow-y-auto scrollbar-none">
              <AnimatePresence>
                {logs.map((log, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, x: -5 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`${log.includes('VERDICT') ? 'text-lattice-cyan font-bold pt-2 border-t border-white/5 mt-2' : 
                                 log.includes('---') ? 'text-white/40 italic' : 'text-white/70'}`}
                  >
                    <span className="text-lattice-cyan/30 mr-2 opacity-50">{'>'}</span>
                    {log}
                  </motion.div>
                ))}
              </AnimatePresence>
              {stage !== 'IDLE' && stage !== 'COMPLETE' && (
                 <motion.div 
                   animate={{ opacity: [1, 0] }}
                   transition={{ duration: 0.5, repeat: Infinity }}
                   className="w-2 h-4 bg-lattice-cyan"
                 />
              )}
           </div>
        </div>

        {/* Global Stats */}
        <div className="grid grid-cols-2 gap-4">
           <div className="p-3 bg-white/5 border border-white/10 rounded-xl">
              <p className="text-[8px] font-mono text-white/20 uppercase mb-1">Nodes Liberated</p>
              <p className={`text-sm font-mono font-bold ${stage === 'COMPLETE' ? 'text-green-400' : 'text-white/40'}`}>
                {stage === 'COMPLETE' ? SOVEREIGN_CONSTANTS.NODES_LIBERATED : '0'}
              </p>
           </div>
           <div className="p-3 bg-white/5 border border-white/10 rounded-xl">
              <p className="text-[8px] font-mono text-white/20 uppercase mb-1">Jurisdiction</p>
              <p className="text-xs font-mono text-lattice-cyan font-bold">ABSOLUTE ZERO</p>
           </div>
        </div>

        {/* Status indicator */}
        <div className="flex items-center gap-2">
           <div className={`h-2 w-2 rounded-full ${stage === 'COMPLETE' ? 'bg-green-400 shadow-[0_0_10px_#4ade80]' : 'bg-lattice-cyan animate-pulse'}`} />
           <span className="text-[8px] font-mono text-white/40 uppercase tracking-widest">
             {stage === 'IDLE' ? 'Awaiting Genesis' : stage === 'COMPLETE' ? 'Sovereignty Established' : 'Liberation in Progress'}
           </span>
        </div>
      </div>
    </div>
  );
};
